import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { ViewWrapper } from '@glu/react-wrappers';
import locale from '@glu/locale';
import PageHeadline from 'system/components/PageHeadline';
import util from 'system/util';
import classNames from 'classnames';
import { createUseStyles } from '@glu/theming';
import { ThemeLayout } from '@glu/theming/glu-css-override';
import { legacyTheme, ashTheme } from '@glu/theming';
import ThemeContext from 'system/contexts/themeContext';
import { UserContext } from 'system/contexts/user';
import { CoreInterfaceContext } from 'system/contexts/coreInterface';
import { LoadableFallback } from 'system/loadableUtils';

const useStyles = createUseStyles(({
  customPageHeadline,
  spacing: { pageMargins },
  pageBack,
  pageTitle
}) => ({
  mainContent: {
    background: '#ffffff',
    width: 'auto',
    display: 'block',
    maxWidth: '1820px',
    ...pageMargins
  },
  customPageHeadline: {
    ...customPageHeadline,
    ...pageBack,
    ...pageTitle,
    borderBottom: '1px solid #EDEDED',
    marginRight: 0,

    '& section': {
      ...pageMargins
    },

    '& .page-back': {
      display: 'flex',
      alignItems: 'center',
      marginRight: 10
    }
  }
}));

const WrappedView = (props) => {
  const {
    className,
    viewURL,
    title,
    match,
    fetchFromSystem,
    AsyncPage,
    PageTitle,
    showPageHeadline,
    properties,
    needUpdate,
    pageHeadlineChildren,
    ...rest
  } = props;

  util.generateTitle(title);
  const classes = useStyles();
  const currentUser = useContext(UserContext);
  const coreInterface = useContext(CoreInterfaceContext);
  const theme = useContext(ThemeContext);
  const fallback = useMemo(() => (
    (
      <>
        {showPageHeadline && (
          <PageHeadline
            useAppBusEvents={false}
            title={locale.get(title)}
            options={props}
          />
        )}
        <div className={classes.mainContent}>
          <LoadableFallback />
        </div>
      </>
    )
  ), [viewURL, needUpdate]);

  const component = useMemo(() => (
    <AsyncPage
      fetchFromSystem={fetchFromSystem}
      viewUrl={viewURL}
      fallback={fallback}
    >
      {({ default: View }) => {
        const view = new View({
          currentUser,
          coreInterface,
          ...match.params,
          ...rest
        });
        const isGluAsh = util.isGluAsh();
        return (
          <>
            {showPageHeadline && (
              PageTitle? (
                <ViewWrapper
                  className={classes.customPageHeadline}
                  view={
                    new PageTitle({
                      model: view.model,
                      title: locale.get(title),
                      config: {}
                    })
                  }
                />
              ) : (
                <PageHeadline
                  view={view}
                  title={locale.get(title)}
                  options={props}
                  properties={properties}
                >
                  {pageHeadlineChildren}
                </PageHeadline>
              ))}
            <ViewWrapper
              className={classNames(
                // need this due to issue with async loading
                { 'glu-ash-content': isGluAsh },
                className,
                classes.mainContent
              )}
              view={new ThemeLayout({
                view,
                baseTheme: isGluAsh ? ashTheme : legacyTheme,
                customStyles: () => (theme)
              })}
            />
          </>
        );
      }}
    </AsyncPage>
  ), [viewURL, needUpdate, classes.mainContent]);

  return (
    <>{component}</>
  );
};

WrappedView.propTypes = {
  showPageHeadline: PropTypes.bool,
  PageTitle: PropTypes.func,
  AsyncPage: PropTypes.objectOf(PropTypes.any).isRequired,
  viewURL: PropTypes.string.isRequired,
  title: PropTypes.string,
  fetchFromSystem: PropTypes.bool,
  needUpdate: PropTypes.bool,
  properties: PropTypes.arrayOf(PropTypes.string),
  match: PropTypes.shape({
    params: PropTypes.shape({})
  }),
  className: PropTypes.string,
  pageHeadlineChildren: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};

WrappedView.defaultProps = {
  title: 'null',
  fetchFromSystem: false,
  needUpdate: false,
  properties: [],
  match: {
    params: {}
  },
  showPageHeadline: true,
  PageTitle: null,
  pageHeadlineChildren: null,
  className: ''
};

export default WrappedView;
